import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Save, Trash2, X, Eye, EyeOff, ArrowUp, ArrowDown } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";

const EMPTY = { name: "", image: "", url: "", ordre: 999, active: true };

export default function AdminLogos() {
    const qc = useQueryClient();
    const [editing, setEditing] = useState(null); // logo object or 'new'
    const [form, setForm] = useState(EMPTY);

    const { data: logos = [], isLoading } = useQuery({
        queryKey: ["admin-client-logos"],
        queryFn: async () => (await api.get("/admin/client-logos")).data,
    });

    const invalidateAll = () => {
        qc.invalidateQueries({ queryKey: ["admin-client-logos"] });
        qc.invalidateQueries({ queryKey: ["client-logos"] });
    };

    const create = useMutation({
        mutationFn: (data) => api.post("/admin/client-logos", data),
        onSuccess: () => {
            toast.success("Logo ajouté");
            setEditing(null);
            invalidateAll();
        },
        onError: (e) => toast.error(formatApiError(e)),
    });
    const update = useMutation({
        mutationFn: ({ id, data }) => api.put(`/admin/client-logos/${id}`, data),
        onSuccess: () => {
            toast.success("Logo enregistré");
            setEditing(null);
            invalidateAll();
        },
        onError: (e) => toast.error(formatApiError(e)),
    });
    const remove = useMutation({
        mutationFn: (id) => api.delete(`/admin/client-logos/${id}`),
        onSuccess: () => {
            toast.success("Logo supprimé");
            invalidateAll();
        },
        onError: (e) => toast.error(formatApiError(e)),
    });

    const startNew = () => {
        const nextOrdre = logos.length > 0 ? Math.max(...logos.map((l) => l.ordre || 0)) + 10 : 10;
        setForm({ ...EMPTY, ordre: nextOrdre });
        setEditing("new");
    };
    const startEdit = (l) => {
        setForm({
            name: l.name || "",
            image: l.image || "",
            url: l.url || "",
            ordre: l.ordre || 0,
            active: !!l.active,
        });
        setEditing(l);
    };
    const cancel = () => {
        setEditing(null);
        setForm(EMPTY);
    };
    const save = () => {
        if (!form.name.trim() || !(form.image || "").trim()) {
            toast.error("Nom et image sont obligatoires");
            return;
        }
        const payload = {
            name: form.name.trim(),
            image: form.image.trim(),
            url: form.url?.trim() || null,
            ordre: Number(form.ordre) || 0,
            active: !!form.active,
        };
        if (editing === "new") create.mutate(payload);
        else update.mutate({ id: editing.id, data: payload });
    };
    const move = (l, delta) => {
        // swap ordre with neighbour
        const sorted = [...logos].sort((a, b) => (a.ordre || 0) - (b.ordre || 0));
        const idx = sorted.findIndex((x) => x.id === l.id);
        const target = sorted[idx + delta];
        if (!target) return;
        update.mutate({
            id: l.id,
            data: { name: l.name, image: l.image, url: l.url || null, ordre: target.ordre, active: !!l.active },
        });
        update.mutate({
            id: target.id,
            data: { name: target.name, image: target.image, url: target.url || null, ordre: l.ordre, active: !!target.active },
        });
    };
    const toggleActive = (l) => {
        update.mutate({
            id: l.id,
            data: { name: l.name, image: l.image, url: l.url || null, ordre: l.ordre || 0, active: !l.active },
        });
    };

    const sorted = [...logos].sort((a, b) => (a.ordre || 0) - (b.ordre || 0));

    return (
        <div data-testid="admin-logos">
            <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                    <h2 className="font-serif text-2xl text-[#D4BC7C]">Logos clients</h2>
                    <p className="text-sm text-[#A0A0A0] mt-2">
                        Ces logos apparaissent dans le carousel «&nbsp;Ils nous font confiance&nbsp;» en bas de la page d&apos;accueil.
                    </p>
                </div>
                <button className="bc-btn bc-btn--sm" onClick={startNew} data-testid="btn-new-logo">
                    <Plus size={14} /> Nouveau logo
                </button>
            </div>

            {editing && (
                <div className="bc-card mt-8" data-testid="logo-form">
                    <div className="flex items-center justify-between">
                        <p className="bc-label">{editing === "new" ? "Nouveau logo" : "Modifier"}</p>
                        <button className="text-[#A0A0A0] hover:text-[#D4BC7C]" onClick={cancel} data-testid="logo-cancel">
                            <X size={18} />
                        </button>
                    </div>

                    <div className="mt-4">
                        <label className="bc-label">Nom du client (raison sociale)</label>
                        <input
                            className="bc-input"
                            placeholder="Ex : Restaurant du Cerf"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            data-testid="logo-name"
                        />
                    </div>

                    <div className="mt-4">
                        <label className="bc-label">Logo (PNG transparent recommandé)</label>
                        <ImageUpload
                            value={form.image}
                            onChange={(next) => setForm({ ...form, image: next || "" })}
                            testId="logo-upload"
                        />
                        <div className="mt-3">
                            <label className="text-[10px] text-[#A0A0A0] uppercase tracking-widest">
                                Ou coller une URL (optionnel)
                            </label>
                            <input
                                className="bc-input mt-1"
                                placeholder="https://…"
                                value={form.image?.startsWith("data:") ? "" : (form.image || "")}
                                onChange={(e) => setForm({ ...form, image: e.target.value })}
                                data-testid="logo-image-url"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-[1fr,140px] gap-4 mt-4">
                        <div>
                            <label className="bc-label">Lien externe (optionnel)</label>
                            <input
                                className="bc-input"
                                placeholder="https://exemple.ch"
                                value={form.url || ""}
                                onChange={(e) => setForm({ ...form, url: e.target.value })}
                                data-testid="logo-url"
                            />
                        </div>
                        <div>
                            <label className="bc-label">Ordre</label>
                            <input
                                type="number"
                                className="bc-input"
                                value={form.ordre}
                                onChange={(e) => setForm({ ...form, ordre: e.target.value })}
                                data-testid="logo-ordre"
                            />
                        </div>
                    </div>

                    <div className="mt-4 flex items-center gap-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={form.active}
                                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                                className="h-4 w-4 accent-[#D4BC7C]"
                                data-testid="logo-active"
                            />
                            <span className="text-sm text-[#F2ECE0]">Actif (visible sur la page d&apos;accueil)</span>
                        </label>
                    </div>

                    <div className="mt-6 flex gap-3">
                        <button className="bc-btn" onClick={save} data-testid="logo-save">
                            <Save size={14} /> Enregistrer
                        </button>
                        <button className="bc-btn bc-btn--ghost" onClick={cancel}>
                            Annuler
                        </button>
                    </div>
                </div>
            )}

            <div className="mt-10 space-y-3">
                {isLoading && <p className="text-[#A0A0A0]">Chargement...</p>}
                {sorted.map((l, i) => (
                    <div
                        key={l.id}
                        className="bc-card flex items-center gap-4 flex-wrap"
                        data-testid={`logo-item-${l.id}`}
                        style={{ opacity: l.active ? 1 : 0.55 }}
                    >
                        <div
                            className="h-14 w-32 flex items-center justify-center border shrink-0"
                            style={{ borderColor: "var(--bc-border)", background: "#0a0a0a" }}
                        >
                            <img
                                src={l.image}
                                alt={l.name}
                                className="h-full w-full object-contain p-2"
                            />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="font-serif text-[#D4BC7C] text-lg truncate">{l.name}</p>
                            <p className="text-xs text-[#A0A0A0] mt-1">
                                Ordre {l.ordre || 0}
                                {l.url && (
                                    <>
                                        {" · "}
                                        <a href={l.url} target="_blank" rel="noreferrer noopener" className="hover:text-[#D4BC7C] underline">
                                            {l.url}
                                        </a>
                                    </>
                                )}
                            </p>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                            <button
                                className="bc-btn bc-btn--sm bc-btn--ghost"
                                onClick={() => move(l, -1)}
                                disabled={i === 0}
                                title="Monter"
                                data-testid={`logo-up-${l.id}`}
                            >
                                <ArrowUp size={14} />
                            </button>
                            <button
                                className="bc-btn bc-btn--sm bc-btn--ghost"
                                onClick={() => move(l, +1)}
                                disabled={i === sorted.length - 1}
                                title="Descendre"
                                data-testid={`logo-down-${l.id}`}
                            >
                                <ArrowDown size={14} />
                            </button>
                            <button
                                className="bc-btn bc-btn--sm bc-btn--ghost"
                                onClick={() => toggleActive(l)}
                                title={l.active ? "Masquer" : "Afficher"}
                                data-testid={`logo-toggle-${l.id}`}
                            >
                                {l.active ? <EyeOff size={14} /> : <Eye size={14} />}
                            </button>
                            <button
                                className="bc-btn bc-btn--sm"
                                onClick={() => startEdit(l)}
                                data-testid={`logo-edit-${l.id}`}
                            >
                                Modifier
                            </button>
                            <button
                                className="bc-btn bc-btn--sm bc-btn--danger"
                                onClick={() => {
                                    if (window.confirm(`Supprimer le logo «${l.name}» ?`)) remove.mutate(l.id);
                                }}
                                data-testid={`logo-delete-${l.id}`}
                            >
                                <Trash2 size={14} />
                            </button>
                        </div>
                    </div>
                ))}
                {!isLoading && logos.length === 0 && (
                    <p className="text-[#A0A0A0]" data-testid="logos-empty">
                        Aucun logo. Cliquez sur «&nbsp;Nouveau logo&nbsp;» pour ajouter le premier.
                    </p>
                )}
            </div>
        </div>
    );
}
